import React, { useEffect, useRef, useState } from "react";
import { Bug, Flag, LifeBuoy, Lightbulb, Mail, MessageSquare, Radio, X } from "lucide-react";
import { createSupportCase, type SupportCategory } from "../api/support";

const categories = [
  { value: "Feedback", icon: MessageSquare, rgb: "34, 211, 238" },
  { value: "Bug", icon: Bug, rgb: "251, 113, 133" },
  { value: "Report", icon: Flag, rgb: "251, 191, 36" },
  { value: "Idea", icon: Lightbulb, rgb: "167, 139, 250" },
  { value: "Contact", icon: Mail, rgb: "52, 211, 153" },
] as const;

export default function SupportDialog({ open, onClose, onCreated, username }: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  username?: string;
}) {
  const [category, setCategory] = useState<SupportCategory | "">("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const sendingRef = useRef(sending);
  onCloseRef.current = onClose;
  sendingRef.current = sending;

  useEffect(() => {
    if (!open) {
      setCategory("");
      setMessage("");
      setError("");
      return;
    }
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => panelRef.current?.focus());
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !sendingRef.current) onCloseRef.current();
      if (event.key !== "Tab" || !panelRef.current) return;
      const controls = Array.from(panelRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), textarea:not([disabled])"));
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);

  if (!open) return null;

  const selectedTone = categories.find((item) => item.value === category);
  const accentStyle = { "--support-accent-rgb": selectedTone?.rgb || "34, 211, 238" } as React.CSSProperties;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!category || message.trim().length < 9 || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setError("");
    try {
      await createSupportCase(category, message.trim());
      onCreated();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not send your message.");
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2400] flex items-end justify-center bg-[#010207]/90 p-0 backdrop-blur-xl sm:items-center sm:p-4" onMouseDown={(event) => { if (event.target === event.currentTarget && !sending) onClose(); }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Blackspace Support" tabIndex={-1} style={accentStyle} className="bs-support-dialog relative flex max-h-[100dvh] w-full max-w-xl flex-col overflow-hidden rounded-t-[1.5rem] text-white outline-none sm:max-h-[min(90dvh,750px)] sm:rounded-[1.5rem]">
        <header className="relative flex items-start gap-3 border-b border-white/[.07] px-5 py-5 sm:px-6">
          <span className="bs-support-emblem grid h-11 w-11 shrink-0 place-items-center rounded-xl"><LifeBuoy size={21} /></span>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold tracking-tight text-slate-50">Blackspace Support</h2>
            <p className="mt-1 text-xs leading-5 text-slate-400">Sent as {username ? `@${username.replace(/^@/, "")}` : "your identity"}. Replies arrive in Messages.</p>
          </div>
          <button type="button" onClick={onClose} disabled={sending} aria-label="Close support" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-white/10 bg-black/25 text-slate-400 transition hover:border-white/20 hover:text-white disabled:opacity-40"><X size={17} /></button>
        </header>
        <form onSubmit={submit} className="relative min-h-0 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          <fieldset>
            <legend className="mb-3 text-xs font-semibold text-slate-300">Choose a category</legend>
            <div className="flex flex-wrap gap-2">
              {categories.map((item) => {
                const Icon = item.icon;
                return <button key={item.value} type="button" onClick={() => { setCategory(item.value); setError(""); }} aria-pressed={category === item.value} style={{ "--category-accent-rgb": item.rgb } as React.CSSProperties} className="bs-support-category inline-flex min-h-10 items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition"><Icon size={15} strokeWidth={2} /><span>{item.value}</span></button>;
              })}
            </div>
          </fieldset>
          <label className="block">
            <span className="mb-2 block text-xs font-semibold text-slate-300">Your message</span>
            <textarea value={message} onChange={(event) => { setMessage(event.target.value.slice(0, 3000)); setError(""); }} maxLength={3000} rows={5} className="bs-support-input min-h-32 w-full resize-y rounded-xl px-4 py-3 text-sm leading-6 outline-none" />
            <span className="mt-1 block text-right text-[11px] text-slate-500">{message.length}/3000</span>
          </label>
          {error && <p role="alert" className="rounded-xl border border-rose-300/25 bg-rose-300/[.07] px-3 py-2 text-xs text-rose-200">{error}</p>}
          <div className="flex justify-end border-t border-white/[.07] pt-4">
            <button type="submit" disabled={sending || !category || message.trim().length < 9} className="bs-support-submit inline-flex min-h-11 items-center gap-2 rounded-xl px-5 text-sm font-bold transition disabled:cursor-not-allowed"><Radio size={17} />{sending ? "Sending…" : "Send to Blackspace"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
